import { useBrandId } from 'AppProvider';
import useSWR from 'swr';
import { CartPrice, CartPriceBookingInput } from 'types/cartPricing';
import fetchCartPrice from './fetchCartPrice';

type UseCartPriceProps = {
  bookings: CartPriceBookingInput[];
  /** The coupon the guest applied, priced onto the bookings it reaches. */
  discountCode?: string;
};

type CartPriceKey = [
  'cart-price',
  string,
  CartPriceBookingInput[],
  string | null,
];

/**
 * The server's price for a set of bookings. There is deliberately no local fallback figure: a price
 * computed in the browser would disagree with the charge the moment a rate ladder or a per-date tax is
 * involved. Nothing is requested while there is nothing to price.
 */
const useCartPrice = ({ bookings, discountCode }: UseCartPriceProps) => {
  const brandId = useBrandId();

  const key: CartPriceKey | null =
    brandId && bookings.length > 0
      ? ['cart-price', brandId, bookings, discountCode ?? null]
      : null;

  const swr = useSWR<CartPrice>(
    key,
    ([, keyBrandId, keyBookings, keyDiscountCode]: CartPriceKey) =>
      fetchCartPrice(keyBrandId, keyBookings, {
        discountCode: keyDiscountCode ?? undefined,
      }),
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
    },
  );

  return { ...swr, cart: swr.data ?? null };
};

export default useCartPrice;
